import { SimpleGrid, Card, CardBody, Skeleton } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import {
  FacilitiesTableRowData,
  FacilitiesTableTotalRowData,
  renderDollarCell,
} from './facilitiesTableOptions';
import { GetBalanceSheetExposureDataQueryGQLType } from './getBalanceSheetExposureData.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider/types';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';

// imprint (master trust customer) is requesting some custom views
// that we do not plan to support formally at current time.  we have
// elected to do hacky solution for time being.
// NOTE: this implementation should be maintained consistently with sendImprintMasterTrustPortfolioOverviewEmail in credit-facility-lambda
const getImprintMasterTrustDetails = ({
  rows,
  balanceSheetImpactCalcs,
  orgSlug,
  creditFacilities,
}: {
  rows: FacilitiesTableRowData[];
  balanceSheetImpactCalcs: GetBalanceSheetExposureDataQueryGQLType | null;
  orgSlug: string;
  creditFacilities: CreditFacility[];
}) => {
  if (orgSlug !== 'imprint') {
    return null;
  }

  if (!balanceSheetImpactCalcs) {
    return null;
  }

  const imprintCitiFacility = creditFacilities.find(
    ({ slug }) => slug === 'imprintCiti1',
  );

  finleyAssert(
    imprintCitiFacility,
    `Expected to find Imprint Citi Facility for Imprint Master Trust Details`,
  );

  const imprintCitiFacilityId = imprintCitiFacility.id;

  const maximumTotalBorrowingBase = rows.reduce(
    (max, { totalBorrowingBase }) => Math.max(max, totalBorrowingBase),
    0,
  );

  const portfolioGlobalOutstandingBalance = _.sumBy(
    rows,
    ({ globalLoanOutstanding }) => globalLoanOutstanding,
  );

  const masterTrustAvailability =
    maximumTotalBorrowingBase - portfolioGlobalOutstandingBalance;

  const imprintLoanTapeLoanProductCalcs = balanceSheetImpactCalcs.balanceSheetImpactCalculations.loanProductCalculations.find(
    ({ loanProductName }) => loanProductName === 'Loan Tape',
  );

  finleyAssert(
    imprintLoanTapeLoanProductCalcs,
    `Expected to find Imprint Loan Tape loan product for Imprint Master Trust Details`,
  );

  const imprintCitiBalanceSheetData = imprintLoanTapeLoanProductCalcs.eligibleExposureForCreditFacilities.find(
    ({ creditFacility }) => creditFacility.id === imprintCitiFacilityId,
  );

  finleyAssert(
    imprintCitiBalanceSheetData &&
      imprintCitiBalanceSheetData.eligibleExposure !== null &&
      imprintCitiBalanceSheetData.eligibleExposure !== undefined,
    `Expected to find balance sheet data for Imprint Citi for Imprint Master Trust Details`,
  );

  const balanceSheetExposure =
    imprintCitiBalanceSheetData.eligibleExposure * 0.95; // multiplying by receivable advance rate

  const masterTrustBalanceSheetAvailability =
    masterTrustAvailability + balanceSheetExposure;

  return {
    balanceSheetExposure,
    masterTrustAvailability,
    masterTrustBalanceSheetAvailability,
  };
};

// Fragments
type OverviewTileProps = {
  children: React.ReactNode;
  label: React.ReactNode;
  isLoading?: boolean;
};
const OverviewTile = ({ children, label, isLoading }: OverviewTileProps) => (
  <Card variant="outline">
    <CardBody display="flex" flexDirection="column" gap="0.5rem">
      <Typography variant="detail-muted">{label}</Typography>
      {isLoading ? (
        <Skeleton height="1.5rem" />
      ) : (
        <Typography variant="base" fontWeight="medium">
          {children}
        </Typography>
      )}
    </CardBody>
  </Card>
);

// Exports
type FacilitiesOverviewTilesProps = {
  creditFacilityCount: number;
  totalsRow: FacilitiesTableTotalRowData;
  rows: FacilitiesTableRowData[];
  balanceSheetImpactCalcs: GetBalanceSheetExposureDataQueryGQLType | null;
  isLoading?: boolean;
};

export const FacilitiesOverviewTiles = ({
  creditFacilityCount,
  totalsRow,
  rows,
  balanceSheetImpactCalcs,
  isLoading,
}: FacilitiesOverviewTilesProps) => {
  const { user } = useUserAuthOrThrow();
  const { facilities: creditFacilities } = useCreditFacilitiesOrThrow();

  const masterTrustDetails = useMemo(
    () =>
      getImprintMasterTrustDetails({
        rows,
        balanceSheetImpactCalcs,
        orgSlug: user.organization.slug,
        creditFacilities,
      }),
    [rows, balanceSheetImpactCalcs, user, creditFacilities],
  );

  return (
    <SimpleGrid columns={{ sm: 2, md: 4, lg: 4, xl: 4 }} gap="1.5rem">
      {user.userType === 'CAPITAL_PROVIDER' ? (
        <>
          <OverviewTile
            label={`Total ${user.organization.name} Commitment`}
            isLoading={isLoading}
          >
            {renderDollarCell(totalsRow.capitalProviderCommitmentAmount)}
          </OverviewTile>
          <OverviewTile
            label={`Total ${user.organization.name} Outstanding`}
            isLoading={isLoading}
          >
            {renderDollarCell(totalsRow.capitalProviderLoanOutstanding)}
          </OverviewTile>
          <OverviewTile
            label={`Total ${user.organization.name} Unused Commitment`}
            isLoading={isLoading}
          >
            {renderDollarCell(totalsRow.capitalProviderUnused)}
          </OverviewTile>
        </>
      ) : (
        <>
          <OverviewTile label="Total Commitment" isLoading={isLoading}>
            {renderDollarCell(totalsRow.globalCommitmentAmount)}
          </OverviewTile>
          <OverviewTile label="Total Outstanding" isLoading={isLoading}>
            {renderDollarCell(totalsRow.globalLoanOutstanding)}
          </OverviewTile>
          <OverviewTile label="Total Unused Commitment" isLoading={isLoading}>
            {renderDollarCell(totalsRow.globalUnusedCommitment)}
          </OverviewTile>
        </>
      )}
      <OverviewTile label="Facilities" isLoading={isLoading}>
        {creditFacilityCount}
      </OverviewTile>
      {masterTrustDetails && (
        <>
          <OverviewTile label="Master Trust Availability" isLoading={isLoading}>
            {renderDollarCell(masterTrustDetails.masterTrustAvailability)}
          </OverviewTile>
          <OverviewTile label="Balance Sheet Exposure" isLoading={isLoading}>
            {renderDollarCell(masterTrustDetails.balanceSheetExposure)}
          </OverviewTile>
          <OverviewTile
            label="Master Trust + Balance Sheet Availability"
            isLoading={isLoading}
          >
            {renderDollarCell(
              masterTrustDetails.masterTrustBalanceSheetAvailability,
            )}
          </OverviewTile>
        </>
      )}
    </SimpleGrid>
  );
};
